import { memo } from "react";

import Stack from "@mui/material/Stack";
import { TicketingFormCategory } from "@simplyk/common";

import { useTranslate } from "@/common/hooks/useTranslate";
import { RateContextProvider } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/Context/RateContext";
import { AmountField } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/RateFields/AmountField/AmountField";
import { DescriptionField } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/RateFields/DescriptionField/DescriptionField";
import { MaxPerOrderField } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/RateFields/MaxPerOrderField/MaxPerOrderField";
import { NameField } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/RateFields/NameField/NameField";
import { RateImagesField } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/RateFields/RateImagesField/RateImagesField";
import { SeatsField } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/RateFields/SeatsField/SeatsField";
import { ExtrasSection } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/ExtrasSection/ExtrasSection";
import { ExtrasSummary } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/ExtrasSummary/ExtrasSummary";
import { RateCardShell } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateCardShell/RateCardShell";
import { RateFieldsRow } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateFieldsRow/RateFieldsRow";
import { TaxReceiptRow } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateOptions/OptionRows/TaxReceiptRow/TaxReceiptRow";
import { useShowTaxReceiptRow } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateOptions/OptionRows/TaxReceiptRow/useShowTaxReceiptRow";
import { TaxRow } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateOptions/OptionRows/TaxRow/TaxRow";
import { useShowTaxRow } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateOptions/OptionRows/TaxRow/useShowTaxRow";
import { RateOptionsGroup } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateOptions/RateOptionsGroup";

export interface AddOnCardProps {
  rateId: string;
  rateIndex: number;
  alreadyBoughtTicketsCount: number;
  isEditing: boolean;
  totalCardNumber: number;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}

const AddOnCardInner = ({ rateId, rateIndex, alreadyBoughtTicketsCount, isEditing, ...cardProps }: AddOnCardProps) => {
  return (
    <RateContextProvider
      rateId={rateId}
      rateIndex={rateIndex}
      formCategory={TicketingFormCategory.Event}
      isEditing={isEditing}
      isOnboarding={false}
      isRegistration={false}
      recurrences={undefined}
      alreadyBoughtTicketsCount={alreadyBoughtTicketsCount}
      isAddOn={true}
    >
      <AddOnCardContent {...cardProps} />
    </RateContextProvider>
  );
};

const AddOnCardContent = (
  cardProps: Omit<AddOnCardProps, "rateId" | "rateIndex" | "alreadyBoughtTicketsCount" | "isEditing">
) => {
  const { t } = useTranslate();
  const showTaxReceiptField = useShowTaxReceiptRow();
  const showTaxField = useShowTaxRow();

  return (
    <RateCardShell
      {...cardProps}
      shouldDisplayDeleteButton
      fallbackName={t("dashboard", "ticketing.addOns.fallbackName")}
      hideIndexInTitle
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Stack sx={{ flexShrink: 0 }}>
            <RateImagesField />
          </Stack>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <NameField
              label={t("dashboard", "ticketing.addOns.titleLabel")}
              placeholder={t("dashboard", "ticketing.addOns.titlePlaceholder")}
            />
            <AmountField />
            <DescriptionField placeholder={t("dashboard", "ticketing.addOns.descriptionPlaceholder")} />
          </Stack>
        </Stack>

        <ExtrasSection
          mode="toggle"
          variant="options-panel"
          optionsLabel={t("dashboard", "ticketing.addOns.options")}
          collapsedSummary={<ExtrasSummary />}
        >
          <RateOptionsGroup
            title={t("dashboard", "ticketing.rateOptionsGroup.capacity")}
            beforeRows={
              <RateFieldsRow>
                <SeatsField
                  tooltip={t("dashboard", "ticketing.addOns.seatsTooltip")}
                  unit={t("dashboard", "ticketing.addOns.seatsUnit")}
                  data-test="add-on-seats-input"
                />
                <MaxPerOrderField
                  label={t("dashboard", "ticketing.addOns.maxPerOrder")}
                  tooltip={t("dashboard", "ticketing.addOns.maxPerOrderTooltip")}
                />
              </RateFieldsRow>
            }
          />
          {(showTaxReceiptField || showTaxField) && (
            <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.taxesAndReceipts")}>
              {showTaxReceiptField && <TaxReceiptRow tooltip={t("dashboard", "ticketing.addOns.taxReceiptTooltip")} />}
              {showTaxField && <TaxRow />}
            </RateOptionsGroup>
          )}
        </ExtrasSection>
      </Stack>
    </RateCardShell>
  );
};

export const AddOnCard = memo(AddOnCardInner);
AddOnCard.displayName = "AddOnCard";
